import { JobShareUrlSchema } from "@artha/api-contracts";
import {
  COPY_URL_PATTERN,
  COPY_URL_WRAPPER_PATTERN,
  COPY_URL_WRAPPER_SUFFIX,
  COPY_URL_BRACKETS,
  COPY_MARKDOWN_PATTERN,
} from "./job-links.constants";

/** Only remove an explicit enclosing delimiter; bare URL punctuation is destination data. */
function trimUrlToken(token: string, prefix: string): string {
  const open = prefix.match(COPY_URL_WRAPPER_PATTERN)?.[0];
  if (!open) return token;
  const close = COPY_URL_BRACKETS[open] ?? open;
  if (open === close) {
    const end = token.lastIndexOf(close);
    return end >= 0 &&
      COPY_URL_WRAPPER_SUFFIX.test(token.slice(end + close.length))
      ? token.slice(0, end)
      : token;
  }
  let depth = 0;
  for (let i = 0; i < token.length; i++) {
    if (token[i] === open) depth++;
    if (token[i] === close && --depth < 0) {
      return token.slice(0, i);
    }
  }
  return token;
}

/** Apply changes around complete URLs, resuming separately at Markdown link destinations. */
function mapCopyUrls(
  text: string,
  mapUrl: (url: string) => string,
  mapText = (value: string) => value,
): string {
  const pattern = new RegExp(COPY_URL_PATTERN);
  let end = 0;
  let result = "";
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    const url = trimUrlToken(match[0], text.slice(0, match.index));
    result += mapText(text.slice(end, match.index)) + mapUrl(url);
    end = pattern.lastIndex = match.index + url.length;
  }
  return result + mapText(text.slice(end));
}

/** Extracts distinct supported job URLs, retaining attribution and encoding verbatim. */
export function extractJobShareUrls(text: string): string[] {
  const urls = new Set<string>();
  mapCopyUrls(text, (url) => {
    if (JobShareUrlSchema.safeParse(url).success) urls.add(url);
    return url;
  });
  return [...urls];
}

/** Replaces whole URL tokens only; overlapping paths and surrounding copy stay intact. */
export function replaceCopyUrls(
  text: string,
  replacements: ReadonlyMap<string, string>,
): string {
  return mapCopyUrls(text, (url) => replacements.get(url) ?? url);
}

/** Keeps the existing plain-text copy format while protecting underscores inside URLs. */
export function stripCopyMarkdown(text: string): string {
  return mapCopyUrls(
    text,
    (url) => url,
    (value) => value.replace(COPY_MARKDOWN_PATTERN, ""),
  );
}
